// Bulk-import submodal for the Manufacturing → Inventory mode.
//
// Accepts pasted JSON, pasted CSV, or an uploaded file (.json/.csv/.txt).
// The pure preview logic lives in `src/lib/manufacturing/bulkImportPreview.ts`;
// this module is just the modal shell + presentation. The server in
// `server/mfg-handlers/createMaterialsBulk.ts` is the source of truth.
//
// Required keys are just `name` + `category` (category drives the auto-code
// prefix). Everything else is optional with sensible defaults.
import { useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { createMaterialsBulk } from "@/lib/manufacturingClient";
import { inputClass, mfgPrimaryBtnLgClass, mfgSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  buildBulkImportPreview,
  OPTIONAL_KEYS,
  REQUIRED_KEYS,
  type ClientPreview,
} from "@/lib/manufacturing/bulkImportPreview";
import { EXAMPLE_BULK_IMPORT_CSV, downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import type {
  MfgBulkCreateMaterialsResponse,
  MfgBulkImportRow,
  MfgMaterial,
} from "@/lib/manufacturing/types";

const EXAMPLE_JSON = `[
  {
    "name": "ProStruct 3/4\\" Flooring",
    "category": "FLOOR",
    "unit": "sheet",
    "shipping_unit": "bundle",
    "shipping_unit_weight": 47.5,
    "pieces_per_shipping_unit": 50,
    "qty": 623,
    "price": 34.97,
    "vendor": "ABC Lumber"
  }
]`;

export function ManufacturingBulkImportModal({
  existingMaterials,
  onClose,
  onImported,
}: {
  existingMaterials: ReadonlyArray<MfgMaterial>;
  onClose: () => void;
  onImported: (summary: MfgBulkCreateMaterialsResponse["summary"]) => void;
}) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const preview = useMemo<ClientPreview>(
    () => buildBulkImportPreview(text, existingMaterials),
    [text, existingMaterials]
  );

  async function handleFile(file: File) {
    setText(await file.text());
  }

  async function handleSubmit() {
    if (preview.kind !== "ok" || preview.validRows.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    const rows: MfgBulkImportRow[] = preview.validRows.map((r) => ({
      name: r.name,
      unit: r.unit,
      category: r.category,
      shipping_unit: r.shipping_unit,
      shipping_unit_weight: r.shipping_unit_weight,
      pieces_per_shipping_unit: r.pieces_per_shipping_unit,
      code: r.code,
      qty: r.qty,
      price: r.price,
      vendor: r.vendor,
      notes: r.notes,
      is_active: r.is_active,
    }));
    const result = await createMaterialsBulk(rows);
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.error || "Bulk import failed");
      return;
    }
    const fails = result.data.summary.createFailures;
    if (fails && fails.length > 0) {
      const sample = fails
        .slice(0, 5)
        .map((f) => `row ${f.idx + 2}${f.code ? ` (${f.code})` : ""}: ${f.error}`)
        .join("; ");
      setSubmitError(
        `Imported with ${fails.length} code collision${fails.length === 1 ? "" : "s"} — fix those rows manually. ${sample}`,
      );
      onImported(result.data.summary);
      return;
    }
    onImported(result.data.summary);
    onClose();
  }

  const importableCount = preview.kind === "ok" ? preview.validRows.length : 0;

  // Keep focus until Cancel or a successful Import — backdrop click used to
  // dismiss (and OS file-picker close often synthesizes a scrim click).
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfg-bulk-import-title"
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl">
        <h3
          id="mfg-bulk-import-title"
          className="text-base font-semibold text-gray-900 dark:text-zinc-100"
        >
          Bulk import materials
        </h3>

        <details className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-300">
          <summary className="cursor-pointer font-medium">Format guide (CSV or JSON)</summary>
          <div className="mt-2 space-y-2">
            <p>
              <strong>Required</strong>: {REQUIRED_KEYS.join(", ")}.{" "}
              <strong>Optional</strong>: {OPTIONAL_KEYS.join(", ")}.
            </p>
            <p>
              <code>code</code> is auto-assigned when omitted (e.g. <code>FLO-000045</code>) using the category
              prefix. When both <code>qty</code> and <code>price</code> are present on a NEW material, an
              opening-balance cost entry seeds WAC for that material in the same import.
            </p>
            <p>
              <strong>Duplicate names</strong> (case-insensitive, trimmed) update the existing material's
              metadata only — on-hand quantities and WAC are preserved. To re-set inventory, use the Receive
              panel or post a cost-entry. Unrecognized keys are silently ignored.
            </p>
            <p>
              <strong>CSV</strong>: header row + one row per material. Quote fields that contain commas, quotes,
              or newlines (escape internal quotes by doubling: <code>""</code>).
            </p>
            <button
              type="button"
              className="text-indigo-700 underline hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200"
              onClick={() => downloadCsv("mfg-materials-import-example.csv", EXAMPLE_BULK_IMPORT_CSV)}
            >
              Download example CSV
            </button>
            <p className="pt-1">
              <strong>JSON</strong> (strict — keys must be quoted with <code>"…"</code>):
            </p>
            <pre className="overflow-x-auto rounded bg-white p-2 text-[11px] dark:bg-zinc-950">{EXAMPLE_JSON}</pre>
          </div>
        </details>

        <div className="mt-4 space-y-3">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Load file (.csv, .json, .txt)
            <input
              type="file"
              accept=".csv,.json,.txt,text/csv,application/json,text/plain"
              className="mt-1 block w-full text-xs text-gray-700 file:mr-2 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100 dark:text-zinc-300"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
            />
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            …or paste CSV / JSON
            <textarea
              className={`${inputClass} mt-1 h-40 font-mono text-[11px]`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste CSV with a header row, or JSON like the example above"
            />
          </label>
        </div>

        {preview.kind !== "empty" ? (
          <div className="mt-4">
            <SectionCard title="Preview">
              <PreviewBody preview={preview} />
            </SectionCard>
          </div>
        ) : null}

        {submitError ? (
          <div className="mt-3 text-xs text-red-600 dark:text-red-400">{submitError}</div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className={mfgSecondaryBtnClass}
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={mfgPrimaryBtnLgClass}
            disabled={submitting || importableCount === 0}
            onClick={() => void handleSubmit()}
          >
            {submitting ? "Importing…" : `Import ${importableCount} row${importableCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewBody({ preview }: { preview: ClientPreview }) {
  if (preview.kind === "empty") return null;
  if (preview.kind === "parse-error") {
    return <div className="text-xs text-red-600 dark:text-red-400">{preview.message}</div>;
  }
  if (preview.kind === "first-row-error") {
    return (
      <div className="text-xs text-red-600 dark:text-red-400">
        First row is missing required keys: {preview.missing.join(", ")}. Add those columns/keys to your{" "}
        {preview.format.toUpperCase()} source and re-paste.
      </div>
    );
  }
  return (
    <div className="space-y-1.5 text-xs text-gray-700 dark:text-zinc-300">
      <div>
        Detected <strong>{preview.format.toUpperCase()}</strong> · <strong>{preview.validRows.length}</strong> of{" "}
        {preview.total} rows ready
        {preview.skipped.length > 0 ? <> · {preview.skipped.length} will be skipped</> : null}.
      </div>
      <div>
        <strong>{preview.willCreate.length}</strong> new
        {preview.willUpdate.length > 0 ? (
          <>
            {" "}
            · <strong>{preview.willUpdate.length}</strong> will overwrite existing (metadata only —
            inventory preserved)
          </>
        ) : null}
        .
      </div>
      {preview.seededCostEntries > 0 ? (
        <div>
          <strong>{preview.seededCostEntries}</strong> rows will seed an opening-balance cost entry (qty + price
          provided on a new material).
        </div>
      ) : null}
      {preview.ignoredKeys.length > 0 ? (
        <div className="text-gray-500 dark:text-zinc-400">
          Unrecognized keys (ignored): {preview.ignoredKeys.join(", ")}
        </div>
      ) : null}
      {preview.willUpdate.length > 0 ? (
        <details className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
          <summary className="cursor-pointer font-medium">
            Show {preview.willUpdate.length} match{preview.willUpdate.length === 1 ? "" : "es"}
          </summary>
          <ul className="mt-1 max-h-32 overflow-y-auto">
            {preview.willUpdate.map((u) => (
              <li key={u.idx}>
                Row {u.idx + 2}: <strong>{u.row.name}</strong> → existing <code>{u.existing.code}</code>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {preview.skipped.length > 0 ? (
        <ul className="mt-1 max-h-32 overflow-y-auto rounded border border-gray-100 bg-gray-50 px-2 py-1 text-[11px] dark:border-zinc-700 dark:bg-zinc-800/40">
          {preview.skipped.map((s) => (
            <li key={s.idx}>
              Row {s.idx + 2}: missing {s.missing.join(", ")}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
